import { Button } from '@/components/button';
import { Dropzone } from '@/components/form/dropzone';
import { FormLabel } from '@/components/form/formlabel';
import { Input } from '@/components/form/input';
import { Modal } from '@/components/modal';
import {
  clearErrors,
  getErrors,
  setOnSuccessUploadBanner,
  setOnSuccessUploadLogo,
  setSenderEmail,
  setWebsite,
} from '@/reducers/profile';
import { AppDispatch } from '@/root/store';
import {
  DeleteWebsiteBanner,
  ShowWebsite,
  UpdateSenderEmail,
  UpdateWebsite,
  UploadWebsiteBanner,
  UploadWebsiteLogo,
} from '@/services/profile';
import {
  DocumentCheckIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/solid';
import { ChevronLeft, ChevronRight, ImagesIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

interface EditImageState {
  banner: File | undefined;
  logo: File | undefined;
}

export function OrgSettingWebsite() {
  const dispatch: AppDispatch = useDispatch();
  const {
    website,
    senderEmail,
    onSuccessUpdateProfile,
    onSuccessUploadLogo,
    onSuccessUploadBanner,
    loading,
    errors,
  } = useSelector((state: any) => state.profile);

  const [currentSlide, setCurrentSlide] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [editImage, setEditImage] = useState<EditImageState>({
    banner: undefined,
    logo: undefined,
  });
  const [bannerImages, setBannerImages] = useState([]);
  const [changePictureModal, setChangePictureModal] = useState(false);
  const [changeBannerModal, setChangeBannerModal] = useState(false);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % website?.banners?.length);
  };

  const prevSlide = () => {
    setCurrentSlide(
      (prev) => (prev - 1 + website?.banners?.length) % website?.banners?.length
    );
  };

  const uploadWebsiteBanner = (e: any) => {
    setBannerImages(e);
    getErrors({});
  };

  const uploadWebsiteLogo = (e: any) => {
    e.forEach((image: File) => {
      setEditImage({
        ...editImage,
        logo: image,
      });
    });

    getErrors({});
  };

  const saveImage = () => {
    if (bannerImages.length > 0) {
      dispatch(UploadWebsiteBanner(website.id, bannerImages));
    }

    if (editImage.logo) {
      dispatch(UploadWebsiteLogo(website.id, editImage.logo));
    }
  };

  const updateWebsite = () => {
    dispatch(UpdateWebsite(website));
  };

  const updateSenderEmail = () => {
    dispatch(UpdateSenderEmail(website, senderEmail));
  };

  const deleteBannerImage = (bannerId: any) => {
    dispatch(DeleteWebsiteBanner(website.id, bannerId));
  };

  const setData = (e: any, element: string, type = 'website') => {
    if (type === 'website') {
      dispatch(setWebsite({ ...website, [element]: e.target.value }));
    } else {
      dispatch(setSenderEmail({ ...senderEmail, [element]: e.target.value }));
    }

    dispatch(clearErrors());
  };

  useEffect(() => {
    if (onSuccessUploadLogo) {
      setChangePictureModal(false);
    }

    if (onSuccessUploadBanner) {
      setChangeBannerModal(false);
    }
  }, [onSuccessUploadBanner, onSuccessUploadLogo]);

  useEffect(() => {
    if (onSuccessUpdateProfile) {
      setEditMode(false);
    }
  }, [onSuccessUpdateProfile]);

  useEffect(() => {
    dispatch(ShowWebsite());
    dispatch(setOnSuccessUploadBanner(false));
    dispatch(setOnSuccessUploadLogo(false));
  }, [onSuccessUploadBanner, onSuccessUploadLogo, onSuccessUpdateProfile]);

  return (
    <>
      <Modal
        show={changePictureModal}
        onRequestClose={() => setChangePictureModal(false)}
        title='Edit foto profil'
        body={
          <>
            <div className={'w-full'}>
              <FormLabel label='Foto profil' errorText={errors.image}>
                <Dropzone onChange={uploadWebsiteLogo} maxFiles={1} />
              </FormLabel>
            </div>
          </>
        }
        footer={
          <div className='flex justify-end gap-3'>
            <Button
              loading={loading}
              onClick={saveImage}
              disabled={!editImage.logo}
              leftIcon={<DocumentCheckIcon />}
            >
              Simpan
            </Button>
          </div>
        }
      />

      <Modal
        show={changeBannerModal}
        onRequestClose={() => setChangeBannerModal(false)}
        title='Edit banner'
        body={
          <>
            <div className={'w-full'}>
              <FormLabel label='Foto banner' errorText={errors.image}>
                <Dropzone onChange={uploadWebsiteBanner} maxFiles={5} />
              </FormLabel>
            </div>
          </>
        }
        footer={
          <div className='flex justify-end gap-3'>
            <Button
              loading={loading}
              onClick={saveImage}
              disabled={bannerImages.length === 0}
              leftIcon={<DocumentCheckIcon />}
            >
              Simpan
            </Button>
          </div>
        }
      />
      <div className='grid grid-cols-1 gap-2 md:grid-cols-2'>
        <div className={'mx-4 my-4 justify-center'}>
          <p className='text-base font-medium text-danger'>Detail Website</p>
          <div className={'mt-4 grid gap-2 md:grid-cols-2'}>
            <div className='mb-3'>
              <FormLabel label='Title' errorText={errors.name} required>
                <Input
                  type='text'
                  disabled={!editMode}
                  value={website.title}
                  onChange={(e) => setData(e, 'title')}
                  required
                />
              </FormLabel>
            </div>
            <div className='mb-3'>
              <FormLabel label='Domain' errorText={errors.domain} required>
                <Input
                  disabled={!editMode}
                  value={website.domain}
                  onChange={(e) => setData(e, 'domain')}
                  type='text'
                  required
                />
              </FormLabel>
            </div>
          </div>
          <div className={'grid gap-2 md:grid-cols-2'}>
            <div className='mb-3'>
              <FormLabel label='SEO Title' errorText={errors.seo_title}>
                <Input
                  type='text'
                  disabled={!editMode}
                  value={website.seo_title}
                  onChange={(e) => setData(e, 'seo_title')}
                />
              </FormLabel>
            </div>
            <div className='mb-3'>
              <FormLabel
                label='SEO Description'
                errorText={errors.seo_description}
              >
                <Input
                  disabled={!editMode}
                  value={website.seo_description}
                  onChange={(e) => setData(e, 'seo_description')}
                  type='text'
                />
              </FormLabel>
            </div>
          </div>
          <div className={'grid gap-2 md:grid-cols-2'}>
            <div className='mb-3'>
              <FormLabel label='SEO Keywords' errorText={errors.seo_keywords}>
                <Input
                  type='text'
                  disabled={!editMode}
                  value={website.seo_keywords}
                  onChange={(e) => setData(e, 'seo_keywords')}
                />
              </FormLabel>
            </div>
            {/* <div className='mb-3'>
              <FormLabel label='Primary Color' errorText={errors.primary_color}>
                <Input
                  disabled={!editMode}
                  value={website.primary_color}
                  onChange={(e) => setData(e, 'primary_color')}
                  type='text'
                  placeholder='#000000'
                />
              </FormLabel>
            </div> */}
          </div>
          <div className='flex justify-end gap-3'>
            {editMode ? (
              <Button
                loading={loading}
                onClick={updateWebsite}
                className={'mt-4 w-full md:w-max'}
                leftIcon={<DocumentCheckIcon />}
              >
                Simpan
              </Button>
            ) : (
              <Button
                variant={'neutral'}
                look={'outline'}
                onClick={() => {
                  setEditMode(true);
                }}
                className={'mt-4 w-full md:w-max'}
                leftIcon={<PencilSquareIcon />}
              >
                Edit
              </Button>
            )}
          </div>
        </div>
        <div className={'mx-4 my-4 justify-center'}>
          <p className='text-base font-medium text-danger'>Emailing</p>
          <div className={'mt-4 grid gap-2 md:grid-cols-2'}>
            <div className='mb-3'>
              <FormLabel label='SMTP Host' errorText={errors.host} required>
                <Input
                  type='text'
                  disabled={!editMode}
                  value={senderEmail.host}
                  onChange={(e) => setData(e, 'host', 'sender_email')}
                  required
                />
              </FormLabel>
            </div>
            <div className='mb-3'>
              <FormLabel label='SMTP Port' errorText={errors.port} required>
                <Input
                  disabled={!editMode}
                  value={senderEmail.port}
                  onChange={(e) => setData(e, 'port', 'sender_email')}
                  type='text'
                  required
                />
              </FormLabel>
            </div>
            <div className='mb-3'>
              <FormLabel
                label='SMTP Username'
                errorText={errors.username}
                required
              >
                <Input
                  disabled={!editMode}
                  value={senderEmail.username}
                  onChange={(e) => setData(e, 'username', 'sender_email')}
                  type='text'
                  required
                />
              </FormLabel>
            </div>
            <div className='mb-3'>
              <FormLabel
                label='SMTP Password'
                errorText={errors.password}
                required
              >
                <Input
                  disabled={!editMode}
                  value={senderEmail.password}
                  onChange={(e) => setData(e, 'password', 'sender_email')}
                  type='password'
                  required
                />
              </FormLabel>
            </div>
            <div className='mb-3'>
              <FormLabel
                label='Sender From Email'
                errorText={errors.email}
                required
              >
                <Input
                  disabled={!editMode}
                  value={senderEmail.email}
                  onChange={(e) => setData(e, 'email', 'sender_email')}
                  type='text'
                  required
                />
              </FormLabel>
            </div>
            <div className='mb-3'>
              <FormLabel
                label='Encryption'
                errorText={errors.encryption}
                required
              >
                <Input
                  disabled={!editMode}
                  value={senderEmail.encryption}
                  onChange={(e) => setData(e, 'encryption', 'sender_email')}
                  type='text'
                  placeholder='ssl'
                  required
                />
              </FormLabel>
            </div>
            <div className='col-span-2 mb-3'>
              <FormLabel label='Sender Name' errorText={errors.name} required>
                <Input
                  disabled={!editMode}
                  value={senderEmail.name}
                  onChange={(e) => setData(e, 'name', 'sender_email')}
                  type='text'
                  required
                />
              </FormLabel>
            </div>
          </div>
          <div className='flex justify-end gap-3'>
            {editMode ? (
              <Button
                loading={loading}
                onClick={updateSenderEmail}
                className={'mt-4 w-full md:w-max'}
                leftIcon={<DocumentCheckIcon />}
              >
                Simpan
              </Button>
            ) : (
              <Button
                variant={'neutral'}
                look={'outline'}
                onClick={() => {
                  setEditMode(true);
                }}
                className={'mt-4 w-full md:w-max'}
                leftIcon={<PencilSquareIcon />}
              >
                Edit
              </Button>
            )}
          </div>
        </div>
      </div>
      <div className={'mx-4 mt-4 text-center md:mx-0 md:mt-0'}>
        <div>
          <div className={'mt-8 flex justify-center'}>
            <img
              src={website.logo?.fallback_url}
              srcSet={website.logo?.srcset}
              alt='Logo'
              className='h-32 w-32 rounded-full bg-purple-500 object-cover text-4xl font-bold'
            />
          </div>
          <div className={'mt-2'}>
            <span
              onClick={() => {
                setChangePictureModal(true);
              }}
              className={'cursor-pointer text-sm font-semibold underline'}
            >
              Edit Logo
            </span>
          </div>
        </div>
        <Button
          variant={'neutral'}
          onClick={() => {
            setChangeBannerModal(true);
          }}
          leftIcon={<ImagesIcon />}
          className={'my-4 cursor-pointer text-sm font-semibold'}
        >
          Tambah Banner Slides
        </Button>
        {website?.banners?.length > 0 && (
          <div className='md:px-8 md:pt-8'>
            <div
              className='relative mx-auto mb-12 max-w-7xl overflow-hidden md:rounded-2xl'
              style={{
                height: '400px',
              }}
            >
              {website.banners?.map((banner: any, index: number) => (
                <div
                  key={index}
                  className={`absolute inset-0 transition-transform duration-700 ease-in-out ${
                    index === currentSlide
                      ? 'translate-x-0'
                      : index < currentSlide
                        ? '-translate-x-full'
                        : 'translate-x-full'
                  }`}
                >
                  {/* Image */}
                  <img
                    src={banner?.fallback_url}
                    srcSet={banner?.srcset}
                    alt='Banner'
                    className='h-full w-full object-cover'
                  />

                  {/* Gradient overlay */}
                  <div className='absolute inset-0 bg-gradient-to-r from-black/60 to-black/10' />

                  {/* Delete button */}
                  <div className='absolute right-2 top-2 z-10'>
                    <Button
                      leftIcon={<TrashIcon className='text-red-500' />}
                      loading={loading}
                      variant={'neutral'}
                      onClick={() => deleteBannerImage(banner.id)}
                    />
                  </div>
                </div>
              ))}
              {/* Navigation Arrows */}
              <button
                onClick={prevSlide}
                className='absolute left-4 top-1/2 hidden -translate-y-1/2 rounded-full bg-white/20 p-2 text-white backdrop-blur-sm transition-all hover:bg-white/30 md:block'
              >
                <ChevronLeft className='h-6 w-6' />
              </button>
              <button
                onClick={nextSlide}
                className='absolute right-4 top-1/2 hidden -translate-y-1/2 rounded-full bg-white/20 p-2 text-white backdrop-blur-sm transition-all hover:bg-white/30 md:block'
              >
                <ChevronRight className='h-6 w-6' />
              </button>

              {/* Slide Indicators */}
              <div className='absolute bottom-6 left-1/2 flex -translate-x-1/2 space-x-2'>
                {website?.banners?.map((_: any, index: number) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`h-3 w-3 rounded-full transition-all ${
                      index === currentSlide
                        ? 'bg-white'
                        : 'bg-white/50 hover:bg-white/75'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
